import { ReactNode } from 'react'
import { styled } from 'styled-components'
import { useTranslation } from 'react-i18next'

import { useBuySellStore, TransactionType } from '@/reducers/transactionReducer'
import { formatDollars } from '@/utils/formatDollars'
import { formatPercent } from '@/utils/formatPercent'
import { useLimitsQuery } from '@/hooks/useLimits'
import { StableCoinValue, StableCoins, Networks } from '@/utils/currencies'

const List = styled.ol`
  display: flex;
  flex-direction: column;
`

const Entry = styled.li`
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  padding-left: 15px;
  position: relative;
  padding-bottom: 10px;
  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 21px;
    height: calc(100% - 20px);
    border-right: 1.5px dotted;
    border-color: var(--rock);
  }
  &:after {
    content: '';
    position: absolute;
    left: calc(0px - 8px);
    top: 3px;
    width: 16px;
    height: 16px;
    border: 2.5px solid;
    border-color: var(--rock);
    border-radius: 50%;
    background: #b7b6b6;
  }
  &:last-child {
    &:before {
      display: none;
    }
  }
  &:first-child:after {
    background: #b7b6b6;
  }
`

export type FeeTerms = { feeRate: number; minFee: number }

export const getSwapFeeText = ({ feeRate, minFee }: FeeTerms) => {
  if (feeRate > 0) {
    return {
      feePercent: formatPercent(feeRate),
      minFee: minFee > 0 ? `(min ${formatDollars(minFee)})` : '',
    }
  }
  return {
    feePercent: '',
    minFee: minFee > 0 ? formatDollars(minFee) : 'No fee',
  }
}

const ConversionRow = ({ currency }: { currency: StableCoinValue }) => {
  const latestV2Quote = useBuySellStore.use.latestV2Quote()
  const network = useBuySellStore.use.network()
  const conversion =
    currency === StableCoins.CADC
      ? '1.0000'
      : (latestV2Quote?.breakdown.usdToCad ?? 0).toFixed(4)
  return (
    <div data-testid="text-conversion-row">
      <span
        className="font-normal leading-3"
        data-testid="text-conversion-label"
      >
        1 {StableCoins[currency as keyof typeof StableCoins]}-{network}
        &nbsp;=&nbsp;
      </span>
      <span
        className="text-clementine dark:text-sun"
        data-testid="text-conversion-value"
      >
        {conversion} CAD
      </span>
    </div>
  )
}

const Title = ({ children }: { children: ReactNode }) => {
  return (
    <div className="text-clementine dark:text-sun" data-testid="text-title">
      {children}
    </div>
  )
}

const SwapTitle = ({ children }: { children: ReactNode }) => {
  return (
    <div className="text-black dark:text-sun" data-testid="text-swap-title">
      {children}
    </div>
  )
}

const SubTitle = ({ children }: { children: ReactNode }) => {
  return (
    <div className="text-xs dark:text-rock" data-testid="text-subtitle">
      {children}
    </div>
  )
}

export const SubTotal = () => {
  const { t } = useTranslation()

  const stablecoin = useBuySellStore.use.stableCoin()
  const latestV2Quote = useBuySellStore.use.latestV2Quote()
  const network = useBuySellStore.use.network()
  const transactionType = useBuySellStore.use.transactionType()
  const limits = useLimitsQuery()

  return (
    <List className="my-auto w-full" data-testid="text-list">
      <Entry
        className="after:bg-cloud after:dark:bg-pebble"
        data-testid="text-entry-conversion"
      >
        <div>
          <ConversionRow currency={stablecoin} />
        </div>
      </Entry>
      {transactionType === TransactionType.BUY &&
        network === Networks.ethereum && (
          <Entry data-testid="text-entry-gas">
            <Title>${(latestV2Quote?.breakdown.gasFee ?? 0).toFixed(2)}</Title>
            <SubTitle>
              {t('subtotal.gasFee', {
                gasPrice: (latestV2Quote?.breakdown.gasPrice ?? 0).toFixed(0),
              })}
            </SubTitle>
          </Entry>
        )}
      <Entry data-testid="text-entry-swap-fee">
        <SwapTitle>
          {latestV2Quote ? (
            t('subtotal.swapFee', getSwapFeeText(latestV2Quote.breakdown))
          ) : limits.isError ? (
            <span data-testid="text-limits-unavailable">
              {t('subtotal.limitsUnavailable')}{' '}
              <button
                type="button"
                className="underline"
                disabled={limits.isFetching}
                onClick={() => limits.refetch()}
                data-testid="btn-limits-retry"
              >
                {t('subtotal.retry')}
              </button>
            </span>
          ) : (
            '—'
          )}
        </SwapTitle>
      </Entry>
    </List>
  )
}
